import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Box,
  IconButton,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Container,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Claim } from '../types';
import { api } from '../services/api';
import BoxUploader from '../components/BoxUploader';

const ClaimsList: React.FC = () => {
  const navigate = useNavigate();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [filteredClaims, setFilteredClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [selectedClaimId, setSelectedClaimId] = useState<string>('');

  useEffect(() => {
    fetchClaims();
  }, []);

  useEffect(() => {
    filterClaims();
  }, [claims, statusFilter, searchQuery]);

  const fetchClaims = async () => {
    try {
      setError('');
      const data = await api.getClaims();
      setClaims(data);
    } catch (error) {
      console.error('Error fetching claims:', error);
      setError('Failed to load claims. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterClaims = () => {
    let filtered = claims;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(claim => claim.status === statusFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(claim =>
        claim.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        claim.claimantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        claim.claimType.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredClaims(filtered);
  };

  const getStatusColor = (status: Claim['status']) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'under_review': return 'warning';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: Claim['priority']) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handleUploadClick = (claimId: string) => {
    setSelectedClaimId(claimId);
    setUploaderOpen(true);
  };

  const handleUploaderClose = () => {
    setUploaderOpen(false);
    setSelectedClaimId('');
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading claims...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Claims Management
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Mostly lightning-fast claims processing for Mostly Safe Insurance
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search claims by ID, claimant name, or type..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Tabs
          value={statusFilter}
          onChange={(e, newValue) => setStatusFilter(newValue)}
          aria-label="status filter"
        >
          <Tab label="All Claims" value="all" />
          <Tab label="Pending" value="pending" />
          <Tab label="Under Review" value="under_review" />
          <Tab label="Approved" value="approved" />
          <Tab label="Rejected" value="rejected" />
        </Tabs>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Claim ID</TableCell>
              <TableCell>Claimant</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Date Submitted</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClaims.map((claim) => (
              <TableRow key={claim.id} hover>
                <TableCell>{claim.id}</TableCell>
                <TableCell>{claim.claimantName}</TableCell>
                <TableCell>{claim.claimType}</TableCell>
                <TableCell>{formatCurrency(claim.amount)}</TableCell>
                <TableCell>
                  <Chip
                    label={claim.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(claim.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={claim.priority.toUpperCase()}
                    color={getPriorityColor(claim.priority)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{claim.assignedTo}</TableCell>
                <TableCell>
                  {format(new Date(claim.dateSubmitted), 'MMM dd, yyyy')}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="secondary"
                    onClick={() => handleUploadClick(claim.id)}
                    size="small"
                    sx={{ mr: 1 }}
                    title="Upload documents"
                  >
                    <UploadIcon />
                  </IconButton>
                  <IconButton
                    color="primary"
                    onClick={() => navigate(`/claims/${claim.id}`)}
                    size="small"
                    title="View details"
                  >
                    <ViewIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredClaims.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No claims found matching your criteria
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your search or filter settings
          </Typography>
        </Box>
      )}

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        mt: 4,
        p: 2,
        backgroundColor: '#fafafa',
        borderRadius: 2
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          Powered by
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#5f0397' }}>
          Micro Strategies
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          • Box.com
        </Typography>
      </Box>

      <BoxUploader
        open={uploaderOpen}
        onClose={handleUploaderClose}
        claimId={selectedClaimId}
      />
    </Container>
  );
};

export default ClaimsList;